import {
  type FocusEvent,
  type Ref,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from "react";

import { PLUGIN_LIBRARY } from "metabase/plugins";
import type { MenuDropdownProps, MenuProps } from "metabase/ui";
import { Box, Menu, Popover } from "metabase/ui";

import type { DataPickerValue } from "../../../DataPicker";
import { useLogRecentItem } from "../../../EntityPicker/hooks/use-log-recent-item";
import { MiniPickerContext, type MiniPickerSearchParams } from "../../context";
import type { MiniPickerItem, MiniPickerPickableItem } from "../../types";
import {
  focusFirstMiniPickerItem,
  getFolderAndHiddenFunctions,
  useGetPathFromValue,
} from "../../utils";
import { MiniPickerListLoader } from "../MiniPickerItemList";
import { MiniPickerPane } from "../MiniPickerPane";

type NonEmptyString<Value extends string> = Value extends "" ? never : Value;

export type MiniPickerPopupProps<PopupLabel extends string = string> =
  | {
      popupType?: "menu";
      popupLabel?: never;
    }
  | {
      popupType: "dialog";
      popupLabel: NonEmptyString<PopupLabel>;
    };

type MiniPickerBaseProps = {
  searchQuery?: string;
  value?: DataPickerValue;
  opened: boolean;
  trapFocus?: boolean;
  onChange: (value: MiniPickerPickableItem) => void;
  onClose: () => void;
  models: MiniPickerPickableItem["model"][];
  onBrowseAll?: () => void;
  shouldHide?: (item: MiniPickerItem | unknown) => boolean;
  includeHiddenSchemas?: boolean;
  shouldShowLibrary?: boolean;
  forceSearch?: boolean;
  showSearchInput?: boolean;
  searchInputPlaceholder?: string;
  searchParams?: MiniPickerSearchParams;
  onSearchResults?: (results: MiniPickerPickableItem[]) => void;
  children?: React.ReactNode;
  menuProps?: MenuProps;
  menuDropdownProps?: MenuDropdownProps;
  closeOnClickOutside?: boolean;
  menuDropdownRef?: Ref<HTMLDivElement>;
  className?: string;
};

export type MiniPickerProps<PopupLabel extends string = string> =
  MiniPickerBaseProps & MiniPickerPopupProps<PopupLabel>;

const MENU_ITEM_SELECTOR = "[data-menu-item]:not([data-disabled])";

function setMenuTabStop(menu: HTMLDivElement, tabStop?: HTMLElement) {
  const menuItems = Array.from(
    menu.querySelectorAll<HTMLElement>(MENU_ITEM_SELECTOR),
  );
  const nextTabStop = tabStop ?? menuItems[0];

  menuItems.forEach((menuItem) => {
    const tabIndex = menuItem === nextTabStop ? 0 : -1;
    if (menuItem.tabIndex !== tabIndex) {
      menuItem.tabIndex = tabIndex;
    }
  });
}

function DialogMenu({
  label,
  children,
}: {
  label: string | undefined;
  children: React.ReactNode;
}) {
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const menu = menuRef.current;
    if (!menu) {
      return;
    }

    const ensureTabStop = () => {
      const activeElement = document.activeElement;
      const activeMenuItem =
        activeElement instanceof HTMLElement &&
        menu.contains(activeElement) &&
        activeElement.matches(MENU_ITEM_SELECTOR)
          ? activeElement
          : undefined;
      setMenuTabStop(menu, activeMenuItem);
    };

    ensureTabStop();

    const observer = new MutationObserver(ensureTabStop);
    observer.observe(menu, {
      attributeFilter: ["tabindex"],
      attributes: true,
      childList: true,
      subtree: true,
    });

    return () => observer.disconnect();
  }, []);

  const handleFocus = useCallback((event: FocusEvent<HTMLDivElement>) => {
    if (
      event.target instanceof HTMLElement &&
      event.target.matches(MENU_ITEM_SELECTOR)
    ) {
      setMenuTabStop(event.currentTarget, event.target);
    }
  }, []);

  return (
    <Box
      ref={menuRef}
      role="menu"
      aria-label={label}
      aria-orientation="vertical"
      data-menu-dropdown
      onFocusCapture={handleFocus}
    >
      {children}
    </Box>
  );
}

export function MiniPicker<const PopupLabel extends string = string>({
  searchQuery,
  value,
  onChange,
  opened,
  onClose,
  models,
  onBrowseAll,
  trapFocus = false,
  shouldHide,
  shouldShowLibrary = true,
  forceSearch = false,
  showSearchInput = false,
  includeHiddenSchemas = false,
  searchInputPlaceholder,
  searchParams,
  onSearchResults,
  children = <Box />,
  menuProps,
  menuDropdownProps,
  closeOnClickOutside = true,
  menuDropdownRef,
  className,
  popupType = "menu",
  popupLabel,
}: MiniPickerProps<PopupLabel>) {
  const { data: libraryCollection } = PLUGIN_LIBRARY.useGetLibraryCollection();

  const [path, setPath, { isLoadingPath }] = useGetPathFromValue({
    value,
    opened,
    libraryCollection,
  });

  const { isFolder, isHidden } = useMemo(() => {
    return getFolderAndHiddenFunctions(models, shouldHide);
  }, [models, shouldHide]);

  useEffect(() => {
    if (trapFocus && path) {
      // any time the path changes, focus the first item
      focusFirstMiniPickerItem();
    }
  }, [path, trapFocus]);

  const { tryLogRecentItem } = useLogRecentItem();

  const handleChange = useCallback(
    async (item: MiniPickerPickableItem) => {
      await onChange(item);
      tryLogRecentItem(item);
    },
    [onChange, tryLogRecentItem],
  );

  return (
    <MiniPickerContext.Provider
      value={{
        path,
        setPath,
        onChange: handleChange,
        isFolder,
        isHidden,
        searchQuery,
        onBrowseAll,
        models,
        canBrowse: !!onBrowseAll,
        libraryCollection,
        shouldShowLibrary,
        forceSearch,
        showSearchInput,
        searchInputPlaceholder,
        searchParams,
        onSearchResults,
        includeHiddenSchemas,
      }}
    >
      <Menu
        opened={opened}
        onChange={onClose}
        closeOnItemClick={false}
        clickOutsideEvents={["mousedown", "touchstart"]}
        closeOnClickOutside={closeOnClickOutside}
        position="bottom-start"
        trapFocus={false}
        {...menuProps}
      >
        {popupType === "dialog" ? (
          <Popover.Target popupType="dialog">{children}</Popover.Target>
        ) : (
          <Menu.Target>{children}</Menu.Target>
        )}

        {popupType === "dialog" ? (
          <Popover.Dropdown
            px={0}
            py="sm"
            data-testid="mini-picker"
            {...menuDropdownProps}
            role="dialog"
            aria-label={popupLabel}
            ref={menuDropdownRef}
          >
            <DialogMenu label={popupLabel}>
              {isLoadingPath ? (
                <MiniPickerListLoader />
              ) : (
                <MiniPickerPane className={className} />
              )}
            </DialogMenu>
          </Popover.Dropdown>
        ) : (
          <Menu.Dropdown
            px={0}
            py="sm"
            data-testid="mini-picker"
            {...menuDropdownProps}
            ref={menuDropdownRef}
          >
            {isLoadingPath ? (
              <MiniPickerListLoader />
            ) : (
              <MiniPickerPane className={className} />
            )}
          </Menu.Dropdown>
        )}
      </Menu>
    </MiniPickerContext.Provider>
  );
}
